import { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { DEFAULT_TABLE_PAGE, DEFAULT_TABLE_PAGE_SIZE } from '../constants';
import { getVisaoGeralElasticidadeTable } from '../services';
import { createInitialQuickFilterFields, useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type {
  VisaoGeralElasticidadeElasticityLevel,
  VisaoGeralElasticidadeTableBody,
  VisaoGeralElasticidadeTableParams,
  VisaoGeralElasticidadeTableResponse,
} from '../types';

const paramsRef: { current: VisaoGeralElasticidadeTableParams } = {
  current: {
    page: DEFAULT_TABLE_PAGE,
    size: DEFAULT_TABLE_PAGE_SIZE,
    sortBy: '',
    direction: 'asc',
    competitorMode: 'TOP',
    shareDisplayMode: 'VALOR',
  },
};

const hasFetchedRef = { current: false };

export const useVisaoGeralElasticidadeTable = () => {
  const tableData = useVisaoGeralElasticidadeStore.getState().tableData;
  const elasticityLevels = useVisaoGeralElasticidadeStore(state => state.tableBody.elasticityLevels);
  const tableBodyDraft = useVisaoGeralElasticidadeStore(state => state.tableBody);
  const appliedTableBody = useVisaoGeralElasticidadeStore(state => state.appliedTableBody);
  const setTableData = useVisaoGeralElasticidadeStore.getState().setTableData;
  const setTableBody = useVisaoGeralElasticidadeStore.getState().setTableBody;
  const setAppliedTableBody = useVisaoGeralElasticidadeStore.getState().setAppliedTableBody;

  const mutation = useMutation({
    mutationFn: async (params: VisaoGeralElasticidadeTableParams) => {
      const tableBody = useVisaoGeralElasticidadeStore.getState().tableBody;
      const response = (await getVisaoGeralElasticidadeTable(tableBody, params)) as VisaoGeralElasticidadeTableResponse;

      return { response, requestedTableBody: tableBody };
    },
    onSuccess: ({ response, requestedTableBody }) => {
      setTableData(response);
      setAppliedTableBody(requestedTableBody);
    },
  });

  const hasPendingFilterChanges = !isEqual(tableBodyDraft, appliedTableBody);

  const fetchTable = useCallback(
    (params: VisaoGeralElasticidadeTableParams) => {
      paramsRef.current = params;
      mutation.mutate(params);
    },
    [mutation],
  );

  const handleUpdateResults = useCallback(() => {
    hasFetchedRef.current = true;

    const nextParams: VisaoGeralElasticidadeTableParams = {
      page: DEFAULT_TABLE_PAGE,
      size: paramsRef.current.size,
      sortBy: paramsRef.current.sortBy,
      direction: paramsRef.current.direction,
    };

    fetchTable(nextParams);
  }, [fetchTable]);

  const handleSortColumn = useCallback(
    (column: string, type?: 'asc' | 'desc') => {
      if (!hasFetchedRef.current) return;

      const direction = type ?? 'asc';
      const nextParams: VisaoGeralElasticidadeTableParams = {
        page: DEFAULT_TABLE_PAGE,
        size: paramsRef.current.size,
        sortBy: column,
        direction,
      };

      fetchTable(nextParams);
    },
    [fetchTable],
  );

  const handleChangePage = useCallback(
    (nextPage: number) => {
      if (!hasFetchedRef.current) return;

      const pageIndex = nextPage - 1;
      const nextParams: VisaoGeralElasticidadeTableParams = {
        ...paramsRef.current,
        page: pageIndex,
      };

      fetchTable(nextParams);
    },
    [fetchTable],
  );

  const handleChangeSize = useCallback(
    (nextSize: number) => {
      if (!hasFetchedRef.current) return;

      const nextParams: VisaoGeralElasticidadeTableParams = {
        ...paramsRef.current,
        page: DEFAULT_TABLE_PAGE,
        size: nextSize,
      };

      fetchTable(nextParams);
    },
    [fetchTable],
  );

  const handleChangeTableBodyRequest = useCallback(
    <K extends keyof VisaoGeralElasticidadeTableBody>(name: K, value: VisaoGeralElasticidadeTableBody[K]) => {
      const currentTableBody = useVisaoGeralElasticidadeStore.getState().tableBody;
      setTableBody({ ...currentTableBody, [name]: value });
    },
    [setTableBody],
  );

  const handleApplyQuickFilter = useCallback(
    (partial: Partial<VisaoGeralElasticidadeTableBody>) => {
      const currentTableBody = useVisaoGeralElasticidadeStore.getState().tableBody;
      setTableBody({ ...currentTableBody, ...partial });
      fetchTable({ ...paramsRef.current, page: DEFAULT_TABLE_PAGE });
    },
    [fetchTable, setTableBody],
  );

  const handleElasticityLevelsChange = useCallback(
    (levels: VisaoGeralElasticidadeElasticityLevel[]) => {
      handleApplyQuickFilter({ elasticityLevels: levels });
    },
    [handleApplyQuickFilter],
  );

  const handleClearQuickFilters = useCallback(() => {
    handleApplyQuickFilter(createInitialQuickFilterFields());
  }, [handleApplyQuickFilter]);

  useEffect(() => {
    handleUpdateResults();
  }, []);

  return {
    tableData,
    elasticityLevels,
    hasPendingFilterChanges,
    appliedElasticityLevels: appliedTableBody.elasticityLevels,
    appliedCpiOperator: appliedTableBody.cpiOperator,
    appliedOptimalCpiOperator: appliedTableBody.optimalCpiOperator,
    handleUpdateResults,
    handleSortColumn,
    handleChangePage,
    handleChangeSize,
    handleChangeTableBodyRequest,
    handleApplyQuickFilter,
    handleElasticityLevelsChange,
    handleClearQuickFilters,
  };
};
